export default class FWSlider {
  constructor(element) {
    this.el = element;
    this.touchstartX = 0;
    this.touchendX = 0;
    this.slides = [...this.el.querySelectorAll('.fwSlider__slide')];
    this.slideWidth = 0;
    this.activeSlides = [];
    this.transition = 'all ease 0.4s';
    this.direction = 'left';
    this.parent = this.el.parentNode;
    this.buttons = this.parent.querySelector('.fwSlider__buttons');
    this.transitioning = false;
    this.slideSpeed = 400;
    this.init();
  }

  init() {
    if (this.slides.length < 3) {
      this.abortInitAndMakeToImage();
      return;
    }

    this.initSlides();
    this.buildSlider();
    this.centerSlide();

    // less invasive than window.addEventListener('resize')
    new ResizeObserver(() => {
      this.slideWidth = this.el.querySelector('.fwSlider__slide').getBoundingClientRect().width;
      this.el.style.left = -18+((this.clientWidth()-this.slideWidth)/2)+(-this.slideWidth*2) +'px';

      this.setButtons(this.el.querySelectorAll('.fwSlider__slide')[2]);
    }).observe(this.el);

    // event handlers

    // reinit slider when a dot is clicked, at correct position
    this.parent.querySelectorAll('.fwSlider__buttons__dots a').forEach(a => a.addEventListener('click', e => {
      const number = parseInt(e.target.getAttribute('data-number'));

      this.initSlidesFromNumber(number);
      this.buildSlider();
      this.centerSlide(true);
    }));

    // deal with buttons
    this.parent.querySelector('.fwSlider__buttons-next').addEventListener('click', () => this.next());
    this.parent.querySelector('.fwSlider__buttons-prev').addEventListener('click', () => this.prev());
    // deal with phone slide
    this.el.addEventListener('touchstart', e => {
      this.touchstartX = e.changedTouches[0].screenX;
    });
    this.el.addEventListener('touchend', e => {
      this.touchendX = e.changedTouches[0].screenX;
      if (this.touchendX < this.touchstartX)
        this.next();
      if (this.touchendX > this.touchstartX)
        this.prev();
    });
    // keyboard support for screenreader/keyboard users tabbed into the active slide
    this.el.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    });
  }

  // Makes `slide` the only one exposed to assistive tech (matches the WAI-ARIA
  // carousel pattern: inactive slides get aria-hidden so screenreaders only
  // ever encounter the currently visible slide, in its real left-to-right order).
  // martha-skip additionally excludes them from the Martha "read aloud" widget
  // (oqaasileriffik.gl/d/martha.js), which reads DOM text directly and does not
  // consult aria-hidden/the accessibility tree.
  setFocusSlide(slide, announce = true) {
    this.el.querySelectorAll('.fwSlider__slide').forEach(s => {
      if (s === slide) return;
      s.classList.remove('focus');
      s.setAttribute('aria-hidden', 'true');
      s.classList.add('martha-skip');
      const link = s.querySelector('a');
      if (link) link.setAttribute('tabindex', '-1');
    });

    slide.classList.add('focus');
    slide.removeAttribute('aria-hidden');
    slide.classList.remove('martha-skip');
    const activeLink = slide.querySelector('a');
    if (activeLink) activeLink.removeAttribute('tabindex');

    if (announce) this.announceSlide(slide);
  }

  announceSlide(slide) {
    const status = this.parent.querySelector('[data-aria-status]');
    if (!status) return;
    const number = parseInt(slide.getAttribute('data-number')) + 1;
    const headline = slide.querySelector('h3');
    const message = headline
      ? `Billede ${number} af ${this.slides.length}: ${headline.textContent.trim()}`
      : `Billede ${number} af ${this.slides.length}`;
    status.textContent = '';
    requestAnimationFrame(() => { status.textContent = message; });
  }

  abortInitAndMakeToImage() {
    // hide buttons, set fallback class on first slide and remove rest.
    this.buttons.style.display = 'none';
	if(this.slides.length > 0){
		this.slides[0].classList.add('fallback');
		this.slides[0].removeAttribute('role');
		this.slides[0].removeAttribute('aria-roledescription');
		this.slides[0].removeAttribute('aria-label');
	}
    this.el.classList.add('fallback');
    this.el.removeAttribute('role');
    this.el.removeAttribute('aria-roledescription');
    this.el.removeAttribute('aria-label');
    this.slides.forEach(f => {
      if (!f.classList.contains('fallback')) f.remove();
    });
  }

  // Wraps `index + offset` into [0, this.slides.length) so the 5-slide window
  // always reflects the true circular neighbours [-2,-1,0,+1,+2] around a center,
  // instead of duplicating the nearest neighbour into both peek slots.
  neighborIndex(index, offset) {
    const n = this.slides.length;
    return ((index + offset) % n + n) % n;
  }

  initSlidesFromNumber(number) {
    if (number < 0) number = 0;

    this.activeSlides = [];
    this.activeSlides.push(this.slides[this.neighborIndex(number, -2)].cloneNode(true));
    this.activeSlides.push(this.slides[this.neighborIndex(number, -1)].cloneNode(true));
    this.activeSlides.push(this.slides[number].cloneNode(true));
    this.activeSlides.push(this.slides[this.neighborIndex(number, 1)].cloneNode(true));
    this.activeSlides.push(this.slides[this.neighborIndex(number, 2)].cloneNode(true));
  }

  initSlides() {
    for (let i = 0; i < this.slides.length; i++) {
      this.slides[i].setAttribute('data-number', i);
    }
    this.activeSlides.push(this.slides[this.neighborIndex(0, -2)].cloneNode(true));
    this.activeSlides.push(this.slides[this.neighborIndex(0, -1)].cloneNode(true));
    this.activeSlides.push(this.slides[0].cloneNode(true));
    this.activeSlides.push(this.slides[this.neighborIndex(0, 1)].cloneNode(true));
    this.activeSlides.push(this.slides[this.neighborIndex(0, 2)].cloneNode(true));
  }

  buildSlider() {
    const tmp = document.createElement('div');
    this.activeSlides.forEach(slide => tmp.append(slide));

    this.el.innerHTML = tmp.innerHTML;
    const rect = this.el.querySelectorAll('.fwSlider__slide')[2].getBoundingClientRect();
    this.slideWidth = rect.width;
  }

  // Builds the off-screen peek slide for one edge of the window, freshly cloned
  // from the real source slide (so its data-number/aria-label are always correct)
  // and marked hidden from assistive tech since it never represents the active slide.
  buildHiddenSlide(index) {
    const slide = this.slides[index].cloneNode(true);
    slide.setAttribute('aria-hidden', 'true');
    slide.classList.add('martha-skip');
    const link = slide.querySelector('a');
    if (link) link.setAttribute('tabindex', '-1');
    return slide;
  }

  resetSlideState(direction) {
    const centerNumber = parseInt(this.el.querySelector('.fwSlider__slide.focus').getAttribute('data-number'));

    if (direction == 'left') {
      this.activeSlides.shift();
      this.el.querySelectorAll('.fwSlider__slide')[0].remove();
      this.el.style.left = -18+((this.clientWidth()-this.slideWidth)/2)+(-this.slideWidth*2) +'px';
      this.el.append(this.buildHiddenSlide(this.neighborIndex(centerNumber, 2)));
    }
    else if (direction == 'right') {
      this.activeSlides.pop();
      this.el.querySelectorAll('.fwSlider__slide')[4].remove();
      this.el.style.left = ((this.clientWidth()-this.slideWidth)/2)+(-this.slideWidth*2) +'px';
      this.el.prepend(this.buildHiddenSlide(this.neighborIndex(centerNumber, -2)));
    }
    this.setActiveButton(centerNumber);
    this.transitioning = false;
  }

  clientWidth() {
    return window.innerWidth
        || document.documentElement.clientWidth
        || document.body.clientWidth;
  }

  next() {
    if (this.transitioning) return;
    // get real slide index
    const slideNumber = parseInt(this.el.querySelectorAll('.fwSlider__slide')[2].getAttribute('data-number'));
    const isLast = slideNumber == this.slides.length-1;
    const isSecondLast = slideNumber == this.slides.length-2;
    const nextSlide = !isLast ? slideNumber + 1 : 0;
    const rightSlide = isLast ? 1 : isSecondLast ? 0 : nextSlide+1;

    this.activeSlides[4] = this.slides[rightSlide].cloneNode(true);

    this.el.querySelectorAll('.fwSlider__slide')[4].innerHTML = this.activeSlides[4].innerHTML;
    this.el.querySelectorAll('.fwSlider__slide')[4].setAttribute('data-number', this.activeSlides[4].getAttribute('data-number'));
    this.el.querySelectorAll('.fwSlider__slide')[4].setAttribute('aria-label', this.activeSlides[4].getAttribute('aria-label'));
    this.slideLeft();
  }

  slideLeft() {
    this.el.style.transition = this.transition;
    this.transitioning = true;

    const slide = this.el.querySelectorAll('.fwSlider__slide')[3];

    this.setFocusSlide(slide);

    // math -_- (window width minus half of slider (to insert to center) and then offset with number of slides)
    this.el.style.left = -18+((this.clientWidth()-this.slideWidth)/2)+(-this.slideWidth*3) +'px';

    setTimeout(() => {
      this.el.style.transition = '';
      this.resetSlideState('left');
    }, this.slideSpeed);
  }

  prev() {
    if (this.transitioning) return;
    // get real slide index
    const slideNumber = parseInt(this.el.querySelectorAll('.fwSlider__slide')[2].getAttribute('data-number'));
    const isFirst = slideNumber == 0;
    const isLast = slideNumber == this.slides.length-1;

    let leftSlide = isFirst ? this.slides.length-2 : isLast ? this.slides.length-3 : slideNumber - 2;
    if (leftSlide < 0) leftSlide = this.slides.length-1;
    this.activeSlides[0] = this.slides[leftSlide].cloneNode(true);

    this.el.querySelectorAll('.fwSlider__slide')[0].innerHTML = this.activeSlides[0].innerHTML;
    this.el.querySelectorAll('.fwSlider__slide')[0].setAttribute('data-number', this.activeSlides[0].getAttribute('data-number'));
    this.el.querySelectorAll('.fwSlider__slide')[0].setAttribute('aria-label', this.activeSlides[0].getAttribute('aria-label'));
    this.slideRight();
  }

  slideRight() {
    this.el.style.transition = this.transition;
    this.transitioning = true;

    const slide = this.el.querySelectorAll('.fwSlider__slide')[1];

    this.setFocusSlide(slide);

    // math -_- (window width minus half of slider (to insert to center) and then offset with number of slides)
    this.el.style.left = -18+((this.clientWidth()-this.slideWidth)/2)+(-this.slideWidth) +'px';

    setTimeout(() => {
      this.el.style.transition = '';
      this.resetSlideState('right');
    }, this.slideSpeed);
  }

  centerSlide(announce = false) {
    const slide = this.el.querySelectorAll('.fwSlider__slide')[2];
    this.setFocusSlide(slide, announce);

    this.setButtons(slide);
    this.setActiveButton(slide.getAttribute('data-number'));
  }

  setButtons(slide) {
    const rect = slide.getBoundingClientRect();
    let buttonWidth = this.buttons.getBoundingClientRect().width;
    this.buttons.style.top = rect.height-51 + 'px';

    this.buttons.style.left = -18+((this.clientWidth()-rect.width)/2)+rect.width-(buttonWidth-13) + 'px';
    // fix for when JS gets it wrong and miscalculates button-width
    setTimeout(() => {
      buttonWidth = this.buttons.getBoundingClientRect().width;
      this.buttons.style.left = -16+((this.clientWidth()-rect.width)/2)+rect.width-(buttonWidth-13) + 'px';
    }, 1);

  }
  setActiveButton(number) {
    this.parent.querySelectorAll('.fwSlider__buttons__dots a').forEach(a => {
      if (a.getAttribute('data-number') == number) {
        a.classList.add('focus');
        a.setAttribute('aria-current', 'true');
      } else {
        a.classList.remove('focus');
        a.removeAttribute('aria-current');
      }
    });
  }
}
